import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { get, uniqBy } from "lodash";

import Loading from "../../../../components/Loading/Loading";
import Colour from "../RICBTEColour/Colour";
import HowMany from "../RICBTEHowMany/HowMany";
import LinkChoice from "./LinkChoice";
import ProductCard from "../../../storeHome/components/productCard/productCard";
import { CartStatus, OrderBuilderStatus } from "../../store/constants";
import { OrderStatus } from "../../../storeHome/store/constants";

import(/* webpackChunkName: "order-builder-link-hearing" */ "./linkHearingItemBuilder.scss");

const FAMILY_CATEGORIES = ["AB_LnkNaida", "AB_LnkSky"];
const TYPE_CATEGORIES = ["AB_LnkM", "AB_LnkCROS"];
const COLOUR_ORDER = {
  AB_LnkNaida: ["P1", "P4", "P6", "P8"],
  AB_LnkSky: ["P1", "P4", "P6", "P8", "T7", "Q3", "T3", "M6", "M7", "M8"],
};
const getCategory = (item, codes) => (item.CategoryCodes || []).find((category) => codes.includes(category.CategoryCode));
const getSide = (item) => (item.Code || "").toUpperCase().endsWith("-R") ? "R" : (item.Code || "").toUpperCase().endsWith("-L") ? "L" : "";

const LinkHearingItemBuilder = ({ cartData, cartStatus, addOrderToCart, orderBuilderStatus, orderBuilderData, setIsStockOrder }) => {
  const history = useHistory();
  const { productId } = useParams();
  const items = useMemo(() => get(orderBuilderData, "Items", []).filter((item) => getCategory(item, FAMILY_CATEGORIES) && getCategory(item, TYPE_CATEGORIES)), [orderBuilderData]);
  const colours = get(orderBuilderData, "Colours", []);
  const family = FAMILY_CATEGORIES.includes(productId) ? productId : "";
  const [linkType, setLinkType] = useState("");
  const [colour, setColour] = useState();
  const [leftOrRight, setLeftOrRight] = useState("");
  const [submittedItems, setSubmittedItems] = useState([]);
  const [errors, setErrors] = useState([]);

  const familyChoices = useMemo(() => uniqBy(items.map((item) => ({ ...getCategory(item, FAMILY_CATEGORIES), ImagePath: item.ImagePath })), "CategoryCode").map((category) => ({ Code: category.CategoryCode, Name: category.CategoryName, ImagePath: category.ImagePath })), [items]);
  const typeChoices = useMemo(() => uniqBy(items.filter((item) => getCategory(item, FAMILY_CATEGORIES).CategoryCode === family).map((item) => ({ ...getCategory(item, TYPE_CATEGORIES), ImagePath: item.ImagePath })), "CategoryCode").map((category) => ({ Code: category.CategoryCode, Name: category.CategoryName, ImagePath: category.ImagePath })), [items, family]);
  const matchingItems = useMemo(() => items.filter((item) => getCategory(item, FAMILY_CATEGORIES).CategoryCode === family && getCategory(item, TYPE_CATEGORIES).CategoryCode === linkType), [items, family, linkType]);
  const colourChoices = useMemo(() => {
    const colourOrder = COLOUR_ORDER[family] || [];
    return uniqBy(matchingItems, "ColourCode")
      .map((item) => colours.find((entry) => entry.Code === item.ColourCode))
      .filter(Boolean)
      .map((colour) => ({ ...colour, Name: `${colour.Name}-${colour.Code}` }))
      .sort((a, b) => colourOrder.indexOf(a.Code) - colourOrder.indexOf(b.Code));
  }, [matchingItems, colours, family]);

  useEffect(() => { setLinkType(""); setColour(); setLeftOrRight(""); setErrors([]); }, [family]);
  useEffect(() => { setColour(); }, [linkType]);
  useEffect(() => {
    if (cartStatus === CartStatus.SUCCESS && submittedItems.length) {
      history.push({ pathname: `/related-products/${submittedItems[0].Code}`, state: { productsAddedToCart: submittedItems } });
    }
  }, [cartStatus, history, submittedItems]);

  const addToOrder = () => {
    const nextErrors = [];
    if (!linkType) nextErrors.push("Please select M or CROS");
    if (!colour) nextErrors.push("Please select a colour");
    if (!leftOrRight) nextErrors.push("Please select at least one side");
    const sides = leftOrRight === "B" ? ["R", "L"] : leftOrRight ? [leftOrRight] : [];
    const order = sides.map((side) => {
      const item = matchingItems.find((entry) => entry.ColourCode === colour && getSide(entry) === side);
      if (!item) nextErrors.push(`No ${side === "R" ? "right" : "left"} item is configured for this selection`);
      const selectedColour = colours.find((entry) => entry.Code === colour);
      return item && { ...item, Quantity: 1, ItemCode: item.Code, Side: side, Type: "Standard", ColourName: selectedColour && selectedColour.Name, ColourRgb: selectedColour && selectedColour.Rgb };
    }).filter(Boolean);
    setErrors(nextErrors);
    if (nextErrors.length) return;
    const currentCart = cartData ? [...cartData] : [];
    order.forEach((item) => {
      const index = currentCart.findIndex((entry) => entry && entry.Code === item.Code && entry.Side === item.Side);
      if (index >= 0) currentCart[index] = { ...currentCart[index], Quantity: Number(currentCart[index].Quantity || 0) + 1 };
      else currentCart.push(item);
    });
    setSubmittedItems(order);
    setIsStockOrder(OrderStatus.REGULAR);
    addOrderToCart(currentCart);
  };

  return <div className="order-builder-main-div link-hearing-item-builder-main-div">
    {orderBuilderStatus === OrderBuilderStatus.LOADING && <Loading />}
    {orderBuilderStatus === OrderBuilderStatus.SUCCESS && <div>
      <h1>Link Hearing Aid / CROS</h1>
      {!family && <Row className="link-hearing-family-list">
        {familyChoices.map((choice, index) => <ProductCard
          key={choice.Code}
          product={{ ...choice, ParentCode: "AB_Link" }}
          index={index}
          isStockOrder={OrderStatus.REGULAR}
          categoryComments={[]}
        />)}
      </Row>}
      {family && <LinkChoice title="Choose a model" choices={typeChoices} selected={linkType} onSelect={setLinkType} />}
      {linkType && <Colour colourCodes={colourChoices} setColourForOrder={setColour} />}
      {family && <HowMany howManyForOrder={leftOrRight === "B" ? 2 : 1} setHowManyForOrder={() => {}} leftorRight={leftOrRight} setLeftOrRight={setLeftOrRight} />}
      {family && errors.length > 0 && <Row className="order-builder-component-title"><Col xs={12} className="alert alert-danger"><p>Please resolve the following errors</p><ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul></Col></Row>}
      {family && <div className="item-builder-add-to-cart-button"><button type="button" className="btn float-right" disabled={cartStatus === CartStatus.LOADING} onClick={addToOrder}>Add to order</button></div>}
    </div>}
  </div>;
};

LinkHearingItemBuilder.propTypes = { cartData: PropTypes.array, cartStatus: PropTypes.string, addOrderToCart: PropTypes.func, orderBuilderStatus: PropTypes.string, orderBuilderData: PropTypes.object, setIsStockOrder: PropTypes.func };
export default LinkHearingItemBuilder;
